import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import ChatBubbleTextOval from '@/components/atom/icon/ChatBubbleTextOval';
import DotMenu from '@/components/atom/icon/DotMenu';
import FavoriteHeart from '@/components/atom/icon/FavoriteHeart';
import { EllipsisTextStyle } from '@/components/atom/text/DefaultText';
import CommunityUserNickName from '@/components/molecules/CommunityUserNickName';
import ProfileImage from '@/components/molecules/ProfileImage';
import DeletedItemByReport from '@/components/templates/mobile/communication/Common/MypageMobileTemplate/DeletedItemByReport';
import { REPORT_TYPE } from '@/components/templates/mobile/communication/Common/ReportTemplate';
import useAuthModal from '@/hooks/useAuthModal';
import { PostReportDataType } from '@/hooks/usePostReportModal';
import { tokenState } from '@/recoil/atoms/common/auth';
import theme from '@/styles/theme';
import { AttachmentBlindType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { BOARD_POST_TAG_TYPE } from '@/types/community';
import { BoardSearchResultVo } from '@/types/search';
import { redirectPostImage, resizingImg } from '@/utils/image';

export interface PostItemProps {
  post: BoardSearchResultVo;
  isMypage?: boolean;
  pageName: string;
  onShowReportModal: (e: React.MouseEvent, data: PostReportDataType) => void;
  containerStyle?: React.CSSProperties;
  onClickPost: (e: React.MouseEvent) => NodeJS.Timeout | undefined;
}

export const getTagColor = (type: BOARD_POST_TAG_TYPE) => {
  switch (type) {
    case BOARD_POST_TAG_TYPE.POPULAR:
      return `
          background: ${theme.color.primary200};
          color: ${theme.color.primary700};
        `;
    case BOARD_POST_TAG_TYPE.RECOMMENDED:
      return `
          background: ${theme.color.blue200};
          color: ${theme.color.blue700};
        `;
    case BOARD_POST_TAG_TYPE.DEFAULT:
    default:
      return `
          background: ${theme.color.gray50};
          color: ${theme.color.gray500};
        `;
  }
};

const SearchPostItem = ({ post, isMypage, onShowReportModal, containerStyle, onClickPost }: PostItemProps) => {
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();

  const profileImage = useMemo(() => {
    return post.communityUser.profileImageUrl;
  }, []);

  const isImageBlind = post.boardPostAttachments[0]?.blindType === AttachmentBlindType.Blinded;

  const onClickDotMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!token) {
      setShowAuthModal(true);
      return;
    }

    onShowReportModal(e, {
      targetId: post.boardPostPid,
      targetType: REPORT_TYPE.BOARD_POST,
      isMine: post.isMine,
    });
  };

  return (
    <div>
      <Link href={`${ROUTES.COMMUNITY.DETAIL.path}/${post.boardPostPid}`}>
        <a role={'presentation'} onClick={(e) => onClickPost(e)}>
          <Container style={containerStyle}>
            {!!post.tags.length && (
              <Tags>
                {post.tags.map((tag, key) => (
                  <Tag key={key} type={tag.type}>
                    {tag.label}
                  </Tag>
                ))}
              </Tags>
            )}
            <BodyWrapper>
              <Body>
                <Title>{post.title}</Title>
                <Content>{post.content}</Content>
              </Body>
              {!!post.boardPostAttachments.length && !isImageBlind && (
                <PostImage>
                  <Image
                    alt={'img'}
                    width={72}
                    height={72}
                    src={resizingImg({
                      originUrl: redirectPostImage(post.boardPostAttachments[0].boardPostAttachmentId),
                      width: 140,
                      height: 140,
                    })}
                    objectFit={'cover'}
                  />
                </PostImage>
              )}
            </BodyWrapper>
            {isMypage && post.blinded && <DeletedItemByReport containerStyle={{ marginBottom: 16 }} />}
            <Bottom>
              <ProfileInfo>
                {!isMypage && (
                  <ProfileImageWrapper>
                    <ProfileImage width={32} height={32} src={profileImage} />
                  </ProfileImageWrapper>
                )}
                <Author>
                  {!isMypage && (
                    <CommunityUserNickName userId={post.communityUser.userId} nickName={post.communityUser.nickname} />
                  )}
                  <Date>{post.formattedCreatedAt}</Date>
                </Author>
              </ProfileInfo>
              <AsideIconWrapper>
                <Icon>
                  <FavoriteHeart color={theme.color.gray300} isActive={post.isLikedByMe} /> {post.likeCount}
                </Icon>
                <Icon>
                  <ChatBubbleTextOval /> {post.boardCommentCount}
                </Icon>
                <ButtonIcon onClick={onClickDotMenu}>
                  <DotMenu />
                </ButtonIcon>
              </AsideIconWrapper>
            </Bottom>
          </Container>
        </a>
      </Link>
    </div>
  );
};

export default SearchPostItem;

const Container = styled.li`
  padding: 24px;
  display: flex;
  flex-direction: column;

  ${({ theme }) => `
    background: ${theme.color.white};
    border-bottom: 8px solid ${theme.color.gray30};
  `};
`;

const Tags = styled.div`
  display: flex;
  gap: 6px;
  padding-bottom: 16px;
`;

const Tag = styled.span<{ type: BOARD_POST_TAG_TYPE }>`
  padding: 5px 10px;
  border-radius: 4px;

  ${({ theme, type }) => `
    ${theme.typography.UI12M};
    ${getTagColor(type)};
  `}
`;

const BodyWrapper = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const Body = styled.div`
  width: 100%;
`;

const Title = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  margin-bottom: 8px;

  ${({ theme }) => `
    ${theme.typography.UI18SB};
    color: ${theme.color.gray700};
  `};
`;

const Content = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI14R};
    ${EllipsisTextStyle};
    color: ${theme.color.gray500};
  `};
`;

const PostImage = styled.div`
  min-width: 72px;
  height: 72px;
  border-radius: 16px;
  margin-left: 16px;
  overflow: hidden;

  ${({ theme }) => `
    border: 1px solid ${theme.color.gray100};
  `}
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ProfileImageWrapper = styled.div`
  margin-right: 12px;
`;

const Author = styled.div`
  flex-direction: column;
  display: flex;
`;

const Date = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI12R};
    color: ${theme.color.gray300};
  `};
`;

const AsideIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;

  &:nth-last-child(2) {
    margin-right: 12px;
  }

  svg {
    margin-right: 8px;
  }

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray300};
  `};
`;

const ButtonIcon = styled(DefaultButtonStyled)`
  padding: 6px;
  border-radius: 6px;
  ${({ theme }) => `
    &: active {
      background: ${theme.color.gray50}
    }
  `};
`;
